import React, { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import 'chart.js/auto';
import { Card, COLOR, TINTA_TENUE, REJILLA, eur, pct, num, sinOrden, rampa, ejes, leyenda, barra } from './ui';

/**
 * Las cards de la fase 1 (las diez de Metabase), con su título en forma de
 * pregunta. Cada una pinta lo que devuelve su endpoint, con los mismos campos
 * que la card de Metabase de la que sale (docs/panel-rendimiento-cartera-plan.md).
 */

// ------------------------------------------------------------- 365 / 373
/** Una línea por cosecha sobre la edad de la cartera. Se usa en las dos curvas. */
const CurvaCosecha = ({ filas, campo, yTitulo }) => {
  const data = useMemo(() => {
    const cosechas = [...new Set(filas.map(f => f.cosecha))].sort();
    const dias = [...new Set(filas.map(f => f.dias_desde_la_carga))].sort((a, b) => a - b);
    const colores = rampa(cosechas.length);
    return {
      labels: dias.map(d => `${d} d`),
      datasets: cosechas.map((c, i) => ({
        type: 'line',
        label: c,
        data: dias.map(d => filas.find(f => f.cosecha === c && f.dias_desde_la_carga === d)?.[campo] ?? null),
        borderColor: colores[i],
        backgroundColor: colores[i],
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0.2,
        spanGaps: false,
      })),
    };
  }, [filas, campo]);
  // Con muchas cosechas la leyenda ocupa más que la gráfica: el orden ya lo da
  // la rampa (oscuro = reciente) y el nombre, el tooltip y la tabla.
  const conLeyenda = data.datasets.length <= 12;
  const options = {
    maintainAspectRatio: false,
    interaction: { mode: 'nearest', intersect: false },
    plugins: {
      legend: conLeyenda
        ? { ...leyenda, labels: { ...leyenda.labels, boxWidth: 8, font: { size: 11 } } }
        : { display: false },
      tooltip: { callbacks: { label: ctx => `Cosecha ${ctx.dataset.label}: ${pct(ctx.parsed.y)}` } },
    },
    scales: ejes(yTitulo, {
      x: {
        grid: { display: false },
        ticks: { color: TINTA_TENUE },
        title: { display: true, text: 'días desde la carga', color: TINTA_TENUE },
      },
    }),
  };
  return <Chart type='line' data={data} options={options} />;
};

export const CosechaCard = ({ estado, data }) => (
  <Card
    titulo='¿Cuánto recupera cada cartera según su edad?'
    lectura='Una línea por mes de carga: cuanto más alta a la misma edad, mejor rinde esa cartera. Las más oscuras son las más recientes.'
    nota='Recobro acumulado sobre deuda asignada. Cada cosecha llega solo a la edad que ha vivido; las de menos de 20 expedientes no salen. Toda la historia, no el periodo elegido.'
    estado={estado}
    vacio={!data?.length}
    filas={data}
    csv='curva-de-cosecha'
    columnas={[
      { key: 'cosecha', label: 'Cosecha' },
      { key: 'dias_desde_la_carga', label: 'Días', fmt: num },
      { key: 'recobro_acumulado_pct', label: 'Recobro acumulado', fmt: pct },
      { key: 'recuperado', label: 'Recuperado', fmt: eur },
      { key: 'expedientes', label: 'Expedientes', fmt: num },
    ]}
    alto={320}
  >
    {data?.length ? <CurvaCosecha filas={data} campo='recobro_acumulado_pct' yTitulo='% recuperado' /> : null}
  </Card>
);

export const PagoMinimoCard = ({ estado, data }) => (
  <Card
    titulo='¿Cuántos expedientes llegan al pago mínimo?'
    lectura='Igual que la curva de cosecha, pero en % de expedientes en vez de dinero.'
    nota='Con MDP (en las notas): los pagos suman el mínimo en 7 días o menos. Sin MDP: basta un pago. Cuenta expedientes, no pagos. Toda la historia.'
    estado={estado}
    vacio={!data?.length}
    filas={data}
    csv='pago-minimo'
    columnas={[
      { key: 'cosecha', label: 'Cosecha' },
      { key: 'dias_desde_la_carga', label: 'Días', fmt: num },
      { key: 'pct_expedientes_con_pago_minimo', label: '% expedientes', fmt: pct },
      { key: 'con_minimo', label: 'Con mínimo', fmt: num },
      { key: 'expedientes', label: 'Expedientes', fmt: num },
    ]}
    alto={320}
  >
    {data?.length ? (
      <CurvaCosecha filas={data} campo='pct_expedientes_con_pago_minimo' yTitulo='% de expedientes' />
    ) : null}
  </Card>
);

/** Barras horizontales de una sola serie: la etiqueta ya dice qué es. */
export const BarrasH = ({ etiquetas, valores, color = COLOR.s1, fmt, titulo }) => (
  <Chart
    type='bar'
    data={{ labels: etiquetas, datasets: [barra(titulo, valores, color)] }}
    options={{
      indexAxis: 'y',
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: ctx => `${titulo}: ${fmt(ctx.parsed.x)}` } },
      },
      scales: {
        x: { beginAtZero: true, grid: { color: REJILLA }, border: { display: false }, ticks: { color: TINTA_TENUE } },
        y: { grid: { display: false }, ticks: { color: TINTA_TENUE } },
      },
    }}
  />
);

// ------------------------------------------------------------------ 367
export const ResolucionCard = ({ estado, data }) => {
  const filas = (data || []).map(f => ({ ...f, resolucion: sinOrden(f.resolucion) }));
  return (
    <Card
      titulo='¿Cómo acaban los expedientes?'
      lectura='Cuántos hay en cada desenlace. «En gestión activa» es lo que todavía puede cobrarse.'
      nota='El cierre se lee del saldo vivo. «Marcado como incobrable» es el estado Fallido: una decisión de gestión, y gana al cobro parcial.'
      estado={estado}
      vacio={!filas.length}
      filas={filas}
      csv='como-acaban'
      columnas={[
        { key: 'resolucion', label: 'Resolución' },
        { key: 'expedientes', label: 'Expedientes', fmt: num },
        { key: 'deuda_asignada', label: 'Asignado', fmt: eur },
        { key: 'recuperado', label: 'Recuperado', fmt: eur },
      ]}
    >
      <BarrasH
        titulo='Expedientes'
        etiquetas={filas.map(f => f.resolucion)}
        valores={filas.map(f => f.expedientes)}
        fmt={num}
      />
    </Card>
  );
};

// ------------------------------------------------------------------ 368
const fechaHora = d =>
  d
    ? new Date(d).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;

export const CanalesCard = ({ estado, data, onRecalcular, recalculando }) => {
  const filas = data?.filas || [];
  const sinCalcular = data && data.expedientes > 0 && data.sin_calculo === data.expedientes;
  const chart = {
    labels: filas.map(f => f.canal),
    datasets: [
      barra(
        'Contactados',
        filas.map(f => f.expedientes_contactados),
        COLOR.s1
      ),
      barra(
        'Con respuesta',
        filas.map(f => f.expedientes_con_respuesta),
        COLOR.s2
      ),
    ],
  };
  return (
    <Card
      titulo='¿Por qué canal contestan los deudores?'
      lectura='Expedientes contactados por cada canal y cuántos respondieron de verdad. La distancia entre las dos barras es lo que se pierde.'
      nota='En llamada, respuesta = descolgada de 45 s o más. La respuesta por SMS se registra desde el 09/10/2026. No es causal: quien paga pronto no llega a recibir llamadas.'
      estado={estado}
      vacio={!filas.length}
      pendiente={sinCalcular ? 'El embudo aún no está calculado para esta cartera. Pulsa «Recalcular embudo».' : null}
      /* De cuándo es el cálculo, y el botón para rehacerlo: sin esto un embudo
         a medio calcular se lee como «no contactamos a nadie». */
      pie={<CanalesPie data={data} onRecalcular={onRecalcular} recalculando={recalculando} />}
      filas={filas}
      csv='embudo-por-canal'
      columnas={[
        { key: 'canal', label: 'Canal' },
        { key: 'expedientes_contactados', label: 'Contactados', fmt: num },
        { key: 'expedientes_con_respuesta', label: 'Con respuesta', fmt: num },
        { key: 'tasa_de_respuesta_pct', label: 'Tasa de respuesta', fmt: pct },
      ]}
    >
      <Chart
        type='bar'
        data={chart}
        options={{
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: leyenda,
            tooltip: {
              callbacks: {
                label: ctx => `${ctx.dataset.label}: ${num(ctx.parsed.y)}`,
                footer: items => {
                  const f = filas[items[0]?.dataIndex];
                  return f?.tasa_de_respuesta_pct != null ? `Tasa de respuesta: ${pct(f.tasa_de_respuesta_pct)}` : '';
                },
              },
            },
          },
          scales: ejes('expedientes'),
        }}
      />
    </Card>
  );
};

const CanalesPie = ({ data, onRecalcular, recalculando }) => {
  if (!data) return null;
  const sinCalcular = data.expedientes > 0 && data.sin_calculo === data.expedientes;
  return (
    <div className='flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500 mt-3'>
      <span>
        {sinCalcular
          ? 'Sin calcular.'
          : `Calculado: ${fechaHora(data.calculado_desde)}${
              data.sin_calculo
                ? ` · ${num(data.sin_calculo)} expedientes sin calcular (cuentan como no contactados)`
                : ''
            }`}
      </span>
      {onRecalcular && (
        <button
          type='button'
          onClick={onRecalcular}
          disabled={recalculando}
          className='px-2 py-1 rounded-lg border border-slate-200 hover:text-slate-800 disabled:opacity-50'
        >
          {recalculando ? 'Recalculando… (≈1 min)' : 'Recalcular embudo'}
        </button>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ 369
export const PrimerPagoCard = ({ estado, data }) => {
  const filas = (data || []).map(f => ({ ...f, dias_hasta_el_primer_pago: sinOrden(f.dias_hasta_el_primer_pago) }));
  return (
    <Card
      titulo='¿Cuánto se tarda en cobrar el primer pago?'
      lectura='Expedientes según los días desde que se cargan hasta su primer cobro: cuanto más a la izquierda, antes.'
      nota='Solo los que han pagado algo. Toda la historia.'
      estado={estado}
      vacio={!filas.length}
      filas={filas}
      csv='dias-al-primer-pago'
      columnas={[
        { key: 'dias_hasta_el_primer_pago', label: 'Días hasta el primer pago' },
        { key: 'expedientes', label: 'Expedientes', fmt: num },
        { key: 'recuperado', label: 'Recuperado', fmt: eur },
      ]}
    >
      <Chart
        type='bar'
        data={{
          labels: filas.map(f => f.dias_hasta_el_primer_pago),
          datasets: [
            barra(
              'Expedientes',
              filas.map(f => f.expedientes),
              COLOR.s1
            ),
          ],
        }}
        options={{
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: ctx => `Expedientes: ${num(ctx.parsed.y)}` } },
          },
          scales: ejes('expedientes'),
        }}
      />
    </Card>
  );
};

// ------------------------------------------------------------------ 370
export const TramosCard = ({ estado, data }) => {
  const filas = (data || []).map(f => ({ ...f, tramo_de_importe: sinOrden(f.tramo_de_importe) }));
  return (
    <Card
      titulo='¿Qué importes de deuda compensa colocar?'
      lectura='El % recuperado según el tamaño de la deuda. Una barra larga es un tramo que rinde.'
      nota='Por la deuda asignada de cada expediente. Toda la historia.'
      estado={estado}
      vacio={!filas.length}
      filas={filas}
      csv='recobro-por-tramo'
      columnas={[
        { key: 'tramo_de_importe', label: 'Tramo' },
        { key: 'expedientes', label: 'Expedientes', fmt: num },
        { key: 'deuda_asignada', label: 'Asignado', fmt: eur },
        { key: 'recuperado', label: 'Recuperado', fmt: eur },
        { key: 'ratio_recobro_pct', label: 'Recobro', fmt: pct },
        { key: 'pct_con_pago', label: 'Con pago', fmt: pct },
      ]}
    >
      <BarrasH
        titulo='Recobro'
        etiquetas={filas.map(f => f.tramo_de_importe)}
        valores={filas.map(f => f.ratio_recobro_pct)}
        fmt={pct}
      />
    </Card>
  );
};

// ------------------------------------------------------------------ 371
/** Con muchas marcas (la cartera entera tiene ~60), el gráfico enseña las 12 de más deuda; la tabla, todas. */
const MAX_MARCAS = 12;

export const MarcasCard = ({ estado, data }) => {
  const filas = data || [];
  const top = filas.slice(0, MAX_MARCAS);
  return (
    <Card
      titulo='¿Qué marcas recuperan mejor?'
      lectura='El % recuperado por marca comercial, de las de más deuda a las de menos.'
      nota={`Marca normalizada: mayúsculas, acentos y sufijos de lote no separan marcas.${
        filas.length > MAX_MARCAS
          ? ` El gráfico enseña las ${MAX_MARCAS} de más deuda; la tabla, las ${filas.length}.`
          : ''
      }`}
      estado={estado}
      vacio={!filas.length}
      filas={filas}
      csv='recobro-por-marca'
      columnas={[
        { key: 'marca', label: 'Marca' },
        { key: 'expedientes', label: 'Expedientes', fmt: num },
        { key: 'deuda_asignada', label: 'Asignado', fmt: eur },
        { key: 'recuperado', label: 'Recuperado', fmt: eur },
        { key: 'ratio_recobro_pct', label: 'Recobro', fmt: pct },
        { key: 'pct_con_pago', label: 'Con pago', fmt: pct },
      ]}
      alto={Math.max(200, top.length * 28)}
    >
      <BarrasH
        titulo='Recobro'
        etiquetas={top.map(f => f.marca)}
        valores={top.map(f => f.ratio_recobro_pct)}
        fmt={pct}
      />
    </Card>
  );
};

// ------------------------------------------------------------------ 372
export const CuentasCard = ({ estado, data }) => (
  <Card
    titulo='¿Cómo va cada cuenta?'
    lectura='Las mismas columnas para cada cuenta del cliente, para cuadrar con su propio sistema.'
    nota='El fee de esta tabla es el del expediente (porcentaje_presupuesto), como en Metabase. La facturación con los precios del sistema está en Dinero.'
    estado={estado}
    vacio={!data?.length}
    filas={data || []}
    csv='detalle-por-cuenta'
    columnas={[
      { key: 'cuenta', label: 'Cuenta' },
      { key: 'expedientes', label: 'Expedientes', fmt: num },
      { key: 'deuda_asignada', label: 'Asignado', fmt: eur },
      { key: 'saldo_vivo', label: 'Saldo vivo', fmt: eur },
      { key: 'recuperado', label: 'Recuperado', fmt: eur },
      { key: 'ratio_recobro_pct', label: 'Recobro', fmt: pct },
      { key: 'fee_devengado', label: 'Fee', fmt: eur },
      { key: 'con_pago', label: 'Con pago', fmt: num },
      { key: 'pct_con_pago', label: '% con pago', fmt: pct },
      { key: 'con_pago_minimo', label: 'Pago mínimo', fmt: num },
      { key: 'pct_con_pago_minimo', label: '% pago mínimo', fmt: pct },
      { key: 'expedientes_con_mdp', label: 'Con MDP', fmt: num },
      { key: 'cerrados_con_cobro', label: 'Cerrados con cobro', fmt: num },
      { key: 'quitas', label: 'Quitas', fmt: eur },
      { key: 'retirado', label: 'Retirado', fmt: eur },
    ]}
    alto={null}
    ancho
  />
);
